import {
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Inject,
    Input,
    OnInit,
    Output
} from '@angular/core';
import { PermissionsModuleAccessLevel } from '@predictiveindex/client-permissions-lib';
import {
    SEARCH_RESULT_TOKEN,
    GlobalSearchResult
} from '@predictiveindex/pi-client-global-search-lib';
import { piIcon } from '@predictiveindex/pi-client-icons';
import {
    IconSize,
    TapestryLayoutService,
    TapestryNotificationService,
    TapestrySidenavService
} from '@predictiveindex/tapestry';
import { InspireAccessStatus } from '../../../constants-types';
import {
    Folder,
    InvitationSource,
    ModuleName,
    OrgMember,
    Person,
    PersonUpdate
} from '../../../core-data-model';
import { BaPerson } from '../../../core-data-model/BehavioralAssessmentPerson.model';
import {
    AssessmentsApi,
    OrgMemberService,
    ReferenceProfileService
} from '../../../core-services';
import { DateFormatUtility, StringUtilities } from '../../../core-utilities';
import { ModuleUserAccessService } from '../../../system-services';
import { TranslationService } from '../../../translations';

@Component({
    selector: 'global-search-person-snapshot',
    templateUrl: './global-search-person-snapshot.component.html',
    styleUrls: ['./global-search-person-snapshot.component.less'],
    standalone: false
})
export class GlobalSearchPersonSnapshotComponent implements OnInit {
    @Input()
    globalSearchObject: GlobalSearchResult;

    @Output()
    closeSideNav = new EventEmitter();

    PermissionsModuleAccessLevel = PermissionsModuleAccessLevel;

    baPerson: BaPerson;
    referenceProfileIcon: piIcon;

    IconSize = IconSize;
    insightsLoading = false;
    learnMoreQueryParam = '?source=global-search-person-panel';
    fullName: string;
    firstName: string;
    email: string;
    /** Indicates if the person has no active BA.
     * Either they have a Pending BA or no BA at all
     */
    personPending = false;
    isAtLeastModuleAdmin: boolean;
    /**
     * Indicates if the reference profile icon shoudl be shown.
     * This is true if the person has a pending BA or has a BSID.
     * If there is no pending BA, no icon should be shown.
     */
    showReferenceProfileIcon: boolean;
    referenceProfilesLink: string;
    referenceProfileName: string;
    assessmentId: string;
    lastSent: string;
    personViewableInInspire = false;
    currentInspireUser: OrgMember;
    currentUserInspireAccessStatus = InspireAccessStatus.Unknown;
    currentUserInspireAccessLevel = PermissionsModuleAccessLevel.Unknown;
    personInspireAccessStatus = InspireAccessStatus.Unknown;

    constructor(
        public TR: TranslationService,
        public layout: TapestryLayoutService,
        private cd: ChangeDetectorRef,
        public stringUtil: StringUtilities,
        public dateFormat: DateFormatUtility,
        public assessmentApi: AssessmentsApi,
        public moduleUserAccess: ModuleUserAccessService,
        public tapestryNotification: TapestryNotificationService,
        public orgMemberService: OrgMemberService,
        private readonly referenceProfileService: ReferenceProfileService,
        private readonly tapestrySidenav: TapestrySidenavService,
        @Inject(SEARCH_RESULT_TOKEN)
        private configInjection?: GlobalSearchResult
    ) {
        if (this.configInjection) {
            this.globalSearchObject = configInjection;
        }
    }

    /** Indicates if the person has no active assessment but does have a pending BA */
    get personBAPending(): boolean {
        return (
            this.baPerson.hasPendingBa &&
            this.baPerson.assessmentSentDate != null
        );
    }

    async ngOnInit() {
        const loggedInUserOrgMember =
            await this.orgMemberService.getLoggedinUserOrgMember();
        this.currentUserInspireAccessStatus =
            loggedInUserOrgMember?.inspireAccessStatus;
        this.currentUserInspireAccessLevel =
            this.moduleUserAccess.getUserAccessLevel(ModuleName.Inspire);

        await this.getPersonInformation();

        this.referenceProfilesLink = `${this.TR.key(
            'jobInsights.referenceProfilesLink'
        )}${this.learnMoreQueryParam}`;
    }

    async getPersonInformation(): Promise<BaPerson> {
        try {
            const baPerson = await this.assessmentApi.getPersonalDetails(
                this.globalSearchObject.id
            );

            this.baPerson = baPerson;
            return this.baPerson;
        } finally {
            // Set person details
            this.email = this.globalSearchObject.email || this.baPerson.email;

            this.fullName =
                this.globalSearchObject.displayName ||
                this.baPerson.name.first + ' ' + this.baPerson.name.last;
            this.firstName =
                this.globalSearchObject.firstName || this.baPerson.name.first;

            // Set the reference profile icon and name
            this.showReferenceProfileIcon =
                this.personBAPending || !!this.baPerson?.bsid;
            this.referenceProfileIcon = this.getReferenceProfileIcon(
                this.baPerson.bsid
                    ? this.baPerson?.referencePattern?.patternNumber
                    : null
            );
            this.referenceProfileName = this.getReferenceProfileName(
                this.baPerson?.referencePattern?.referencePatternNameKey
            );

            // If the person is pending - they have been sent a BA but have no reference pattern yet
            // then set up the option to resend the assessment
            this.personPending = this.personBAPending || !this.baPerson?.bsid;

            if (this.personPending) {
                this.lastSent =
                    this.baPerson?.assessmentSentDate !== null
                        ? this.getDate(this.baPerson?.assessmentSentDate)
                        : undefined;
                this.assessmentId = this.baPerson.assessmentId;
                this.isAtLeastModuleAdmin =
                    this.moduleUserAccess.hasAccessLevelForAnyModule(
                        PermissionsModuleAccessLevel.AllModuleAdmin
                    );
            }

            // Determines if there should be the option to invite to inspire
            this.personInspireAccessStatus =
                await this.getPersonInspireAccessStatus(
                    this.baPerson.assessmentUserId
                );
            this.personViewableInInspire =
                this.personInspireAccessStatus ===
                InspireAccessStatus.HasAccess;
        }
    }

    getDate(date: Date) {
        return date
            ? this.dateFormat.getMonthNameDayYearFormat(new Date(date))
            : this.dateFormat.getMonthNameDayYearFormat(
                  new Date(this.globalSearchObject.createdDate)
              );
    }

    getReferenceProfileIcon(patternNumber?: number): piIcon {
        return this.referenceProfileService.mapReferenceProfileIdToIconName(
            patternNumber
        );
    }

    getReferenceProfileName(name: string) {
        return name ? name : 'common.pending';
    }

    async sendBehavioralAssessment() {
        if (!this.baPerson) {
            return;
        }

        this.tapestryNotification.create({
            message: this.TR.key('snackbar.generic.processing-message'),
            duration: 3000,
            type: 'processing'
        });
        try {
            const person = this.getPersonFromBaPerson();
            await this.assessmentApi.createSurveyUserAndSendBehavioralAssessment(
                person,
                InvitationSource.Admin,
                false
            );
            this.tapestryNotification.create({
                message: this.TR.key('sendassessment.savesuccess-message'),
                duration: 3000,
                type: 'success'
            });
            this.orgMemberService.closeSidenav();
        } catch (error) {
            this.tapestryNotification.create({
                message: this.TR.key('notification.error-genericmessage'),
                duration: 3000,
                type: 'error'
            });
            throw error;
        }
    }

    async resendBAAssessment() {
        return this.assessmentApi
            .updateBehavioralAssessmentAndResend(this.assessmentId)
            .then(() => {
                this.tapestryNotification.create({
                    message: this.TR.key('text.common-terms.assessment.resent'),
                    duration: 3000,
                    type: 'success'
                });
                this.orgMemberService.closeSidenav();
            })
            .catch((reason: string) => {
                this.tapestryNotification.create({
                    message: this.TR.key('text.error.body'),
                    duration: 3000,
                    type: 'error'
                });
                throw new Error(reason);
            });
    }

    canSendBA(): boolean {
        const isAdmin = this.moduleUserAccess.hasAccessLevelForAnyModule(
            PermissionsModuleAccessLevel.AllModuleAdmin
        );
        const isInspireLimitedUser =
            this.currentUserInspireAccessLevel <=
            PermissionsModuleAccessLevel.LimitedAccess;
        const canSpendInspireVMs = this.moduleUserAccess.getAllowVMAccess(
            ModuleName.Inspire
        );

        // Admins can always send BAs. Otherwise, Inspire Limited users need VM access.
        return isAdmin || !isInspireLimitedUser || canSpendInspireVMs;
    }

    shouldShowInspireCTA() {
        // person isn't pending, logged in user has access to Inspire, and person doesn't have inspire access
        return (
            this.personPending &&
            (this.currentUserInspireAccessStatus ===
                InspireAccessStatus.HasAccess ||
                this.currentUserInspireAccessStatus ===
                    InspireAccessStatus.Pending) &&
            this.personInspireAccessStatus === InspireAccessStatus.NoAccess
        );
    }

    updateInsightsLoading(loading: boolean) {
        this.insightsLoading = loading;
        // Added detection because 'loading' can change asynchronously during the angular life cycle
        // this causes an ExpressionChangedAfterItHasBeenChecked error.  This prevents that.
        this.cd.detectChanges();
    }

    async getPersonInspireAccessStatus(
        surveyUserGuid: string
    ): Promise<InspireAccessStatus> {
        const orgMember =
            await this.orgMemberService.getOrgMember(surveyUserGuid);
        return orgMember?.inspireAccessStatus;
    }

    closePeekPanel(): void {
        this.tapestrySidenav.close();
        this.closeSideNav.emit();
    }

    /**
     * Creates a PersonUpdate object from the BaPerson data.
     */
    private getPersonFromBaPerson(): PersonUpdate {
        // This probably isn't the prettiest approach but does get the required details
        // for sending a BA to an existing person. It is kept in this component since
        // it's not clear if it's needed elsewhere.
        return new PersonUpdate({
            originalObject: new Person({
                id: this.baPerson.assessmentUserId
            }),
            firstName: this.baPerson.name.first,
            lastName: this.baPerson.name.last,
            email: this.baPerson.email,
            folder: new Folder({
                id: this.baPerson.folderId
            }),
            type: this.baPerson.type
        });
    }
}
